"use client";

import { useEffect, useState } from "react";

/**
 * Two-stage load for a full-resolution viewer that opens **over a surface which
 * already painted a smaller preset of the same photograph**.
 *
 * THE PROBLEM. The listing-detail slide paints `gallerySlideUrl(...)` — the
 * `CARD` preset, ~33 KB, sitting in the browser HTTP cache. Tapping it opens
 * `ImageGalleryModal`, which correctly renders the **full original** (a
 * pinch-zoom viewer must have the real pixels — handoff §11 exempts these two
 * modals from the resize sweep on purpose). But the original is a DIFFERENT URL
 * string, so the browser shares nothing with the frame the user is already
 * looking at: it throws away a decoded image and shows a spinner while it
 * downloads a picture the user can already see.
 *
 * THE FIX. Paint the already-cached preset immediately, load the original in
 * parallel, swap when it has decoded. Zero wait, zero extra bytes for the first
 * frame, and the viewer still ends up on the original — this is a latency
 * change, not a quality one.
 *
 * WHY THE PREVIEW IS AN ARGUMENT AND NOT COMPUTED HERE. The cache hit depends
 * on **byte-identical URL strings** (`lib/images/presets.ts`: "one differing
 * character and it is a fresh ~33 KB download with no runtime symptom"). Only
 * the calling surface knows which preset it painted — the listing galleries
 * painted `CARD`, the chat gallery painted something else, some callers painted
 * nothing at all. A preset guessed in here would be a FOURTH url for the same
 * photo: more bytes, more billed transformations, and a placeholder that is
 * never a cache hit anywhere. So the caller hands in the exact string it
 * already requested, or hands in nothing.
 *
 * NO CROSSFADE, DELIBERATELY. The two stages are the same photograph at two
 * resolutions. `decode()` runs before the swap, so the browser has the full
 * frame ready and the `<img>`'s pending-request mechanism keeps the preview
 * painted until it can replace it in one go — the transition reads as the image
 * getting sharper. A fade between two near-identical frames reads as a flicker.
 *
 * @see lib/listings/gallery-image-urls.ts — where the listing galleries' preview string comes from
 * @see .tondocs/images/HANDOFF_IMAGE_PIPELINE_STANDARDISATION_2026_07_28.md §11, §12.1, §13
 */
export type ProgressiveImage =
  /** One stage: there is nothing cached to show first, so `src` IS the original. */
  | { kind: "single"; src: string }
  /** Showing the caller's already-cached preset; `full` is downloading. */
  | { kind: "preview"; src: string; full: string }
  /** The original has decoded and is what is on screen. */
  | { kind: "full"; src: string };

/**
 * Is a two-stage load worth running at all?
 *
 * Pure so the decision is testable without a renderer, and so all three "no"
 * cases are stated once instead of drifting between the two modals:
 *
 * - **no preview** — the caller painted nothing we could reuse (chat bubbles,
 *   my-listings, the preorder timeline);
 * - **preview === full** — the calling surface already requested the original
 *   itself, so it is in cache and the single-stage render is instantaneous.
 *   This is not a hypothetical: the mobile listing gallery is exactly here
 *   until its slide moves onto a preset (handoff §9 step 6);
 * - **no full** — nothing to upgrade to.
 *
 * Note what is NOT decided here: whether the source is a HEIC blob. That is the
 * caller's call, because only the caller knows the RAW reference behind the
 * rendered src — see the guard in `ImageGalleryModal` / `ImageZoomModal`.
 */
export function canUpgradeFromPreview(
  full: string,
  preview: string | undefined | null,
): preview is string {
  return Boolean(full) && Boolean(preview) && preview !== full;
}

/**
 * @param full    the original — what the viewer must end up showing
 * @param preview the byte-identical string the calling surface already painted,
 *                or `undefined` when it painted nothing reusable
 * @param options `enabled` gates the PREFETCH. **Required, deliberately** — it
 *                has no default, so every call site has to answer the question.
 *
 * Why required rather than `enabled = true`: this hook is used by viewers, and
 * a viewer is normally MOUNTED WHILE CLOSED behind an early
 * `if (!isOpen) return null`. Hooks run before that return, so a defaulted
 * `enabled` would silently start downloading the untouched original on every
 * pageview of any page that merely mounts the viewer — no visible symptom,
 * nothing in the console, visible only in the network panel and the bill. That
 * is the exact defect class this whole change set exists to remove, and it is
 * what happened here on the listing-detail route (twice per view, since the two
 * responsive galleries are toggled with `display:none` and both mount).
 *
 * A default would have made the safe path the one you get by NOT thinking. Pass
 * `enabled: isOpen` from a viewer, `enabled: true` from an always-visible one.
 */
export function useProgressiveImage(
  full: string,
  preview: string | undefined | null,
  options: { enabled: boolean },
): ProgressiveImage {
  const enabled = options.enabled;
  const twoStage = canUpgradeFromPreview(full, preview);

  /**
   * The ONE state slot: which original has finished decoding. Everything else
   * is derived at render, so `preview`/`full`/`decoded` cannot disagree — and
   * comparing it against the CURRENT `full` is what makes a stale value from
   * the previous photo self-invalidating when the user swipes.
   */
  const [decodedSrc, setDecodedSrc] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) return;
    if (!twoStage) return;
    if (typeof window === "undefined") return;

    let cancelled = false;
    const commit = () => {
      if (!cancelled) setDecodedSrc(full);
    };

    // A bare `new Image()` with no `crossOrigin` — it MUST match the attributes
    // of the `<img>` that renders it, or the browser treats the two as separate
    // cache entries and downloads the original twice.
    const loader = new window.Image();
    loader.onload = commit;
    // No `onerror` handler on purpose: on a failed original we never leave the
    // preview. The sharp-enough frame already on screen is worth more to the
    // user than a broken-image icon, and staying put matches what the viewer
    // did before this existed — it never had an error state either.
    loader.src = full;

    // `decode()` is the point of the exercise: it guarantees the swap happens
    // on a frame the browser can paint immediately, so there is no blank gap
    // between the two stages. Not universally available (and it rejects on a
    // load failure), hence both the `onload` belt above and the swallowed
    // rejection here.
    void loader.decode?.().then(commit, () => {});

    return () => {
      cancelled = true;
      loader.onload = null;
    };
    // `enabled` MUST be in here. It is the only dep that moves when a viewer
    // opens: the modal is mounted while closed (see the doc above), so `full`
    // and `twoStage` are already at their final values by the time the user
    // taps. An effect that reads `enabled` but does not depend on it therefore
    // never re-runs after the gate opens — the prefetch that `enabled` exists
    // to DELAY is silently turned into one that never happens, and the viewer
    // stays on the ~828px preview for as long as it is open. The symptom is a
    // soft one (a slightly blurry fullscreen, no error, no failed request),
    // which is exactly why it needs pinning rather than watching.
    // Re-running on `enabled` is safe in both directions: false→true starts the
    // loader, true→false runs the cleanup below, and `decodedSrc` is left alone
    // on purpose so reopening the same photo renders the original immediately.
  }, [full, twoStage, enabled]);

  if (!twoStage) return { kind: "single", src: full };
  if (decodedSrc === full) return { kind: "full", src: full };
  return { kind: "preview", src: preview, full };
}
